// Täckningsvy: vad som saknas i projektet utifrån vald detaljnivå.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Circle,
  Clock,
  ExternalLink,
  RotateCcw,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DETAIL_LEVEL_LABELS } from "@/lib/basProcess/detailLevel";
import { setStepApplicable } from "@/lib/basProcess/simpleMode";
import { fetchCoverage, type CoverageItem, type CoverageResult } from "@/lib/basProcess/coverage";

interface Props {
  projectId: string;
  /** Öppnar rätt flik i Bas-processen. */
  onOpenLink?: (link: string) => void;
}

const StatusIcon: React.FC<{ status: CoverageItem["status"] }> = ({ status }) => {
  if (status === "done") return <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />;
  if (status === "in_progress") return <Clock className="h-4 w-4 shrink-0 text-muted-foreground" />;
  return <Circle className="h-4 w-4 shrink-0 text-muted-foreground" />;
};

const ItemRow: React.FC<{
  item: CoverageItem;
  onOpenLink?: (link: string) => void;
  onRestore?: (doc: string) => void;
}> = ({ item, onOpenLink, onRestore }) => (
  <div className="flex flex-col gap-2 rounded-lg border bg-background p-3 sm:flex-row sm:items-center sm:justify-between">
    <div className="flex min-w-0 gap-2">
      <StatusIcon status={item.status} />
      <div className="min-w-0">
        <p className="text-sm font-medium">
          Dok {item.docNumber} · {item.title}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-1">
          <Badge variant="outline" className="text-xs">
            {item.owner}
          </Badge>
          {item.alwaysRequired && (
            <Badge variant="destructive" className="text-xs">
              Krävs alltid
            </Badge>
          )}
          {item.afs.map((a) => (
            <Badge key={a} variant="secondary" className="text-xs">
              {a}
            </Badge>
          ))}
        </div>
        {item.notApplicableReason && (
          <p className="mt-1 text-xs text-muted-foreground">{item.notApplicableReason}</p>
        )}
      </div>
    </div>
    <div className="flex shrink-0 gap-2">
      {onRestore && (
        <Button variant="outline" size="sm" onClick={() => onRestore(item.docNumber)}>
          <RotateCcw className="mr-1 h-3.5 w-3.5" /> Gör aktuell igen
        </Button>
      )}
      {item.appLink && onOpenLink && (
        <Button variant="ghost" size="sm" onClick={() => onOpenLink(item.appLink as string)}>
          <ExternalLink className="mr-1 h-3.5 w-3.5" /> Öppna
        </Button>
      )}
    </div>
  </div>
);

const Section: React.FC<{
  title: string;
  count: number;
  children: React.ReactNode;
  tone?: "warning";
}> = ({ title, count, children, tone }) => {
  if (count === 0) return null;
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        {tone === "warning" && <AlertTriangle className="h-4 w-4 text-destructive" />}
        <h3
          className={`text-sm font-semibold ${tone === "warning" ? "text-destructive" : "text-foreground"}`}
        >
          {title} ({count})
        </h3>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
};

const CoveragePanel: React.FC<Props> = ({ projectId, onOpenLink }) => {
  const [data, setData] = useState<CoverageResult | null>(null);
  const [owner, setOwner] = useState<string>("alla");
  const [phase, setPhase] = useState<string>("alla");
  const [showInactive, setShowInactive] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setData(await fetchCoverage(projectId));
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const filter = useCallback(
    (items: CoverageItem[]) =>
      items.filter(
        (i) => (owner === "alla" || i.owner === owner) && (phase === "alla" || i.phase === phase)
      ),
    [owner, phase]
  );

  const restore = async (doc: string) => {
    setBusy(true);
    try {
      await setStepApplicable(projectId, doc, true);
      toast.success(`Dok ${doc} är aktuell igen`);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Kunde inte ändra steget");
    } finally {
      setBusy(false);
    }
  };

  const critical = useMemo(() => (data ? filter(data.criticalMissing) : []), [data, filter]);
  const missing = useMemo(
    () => (data ? filter(data.missing).filter((i) => !i.alwaysRequired) : []),
    [data, filter]
  );
  const inProgress = useMemo(() => (data ? filter(data.inProgress) : []), [data, filter]);
  const inactive = useMemo(() => (data ? filter(data.notApplicable) : []), [data, filter]);

  if (!data) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Täckning i processen</CardTitle>
        <p className="text-sm text-muted-foreground">
          Utifrån detaljnivå <strong>{DETAIL_LEVEL_LABELS[data.level]}</strong> är{" "}
          {data.applicable.length} steg aktuella i det här projektet. {data.done.length} är klara och{" "}
          {data.missing.length + data.inProgress.length} återstår.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-2">
          <Progress value={data.percent} />
          <p className="text-xs text-muted-foreground">{data.percent}% av processen är klar.</p>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {data.byPhase.map((p) => (
            <div key={p.key} className="rounded-lg border bg-muted/40 p-2 text-center">
              <p className="text-xs text-muted-foreground">{p.label}</p>
              <p className="text-sm font-semibold">
                {p.done}/{p.total}
              </p>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-2 sm:flex-row">
          <Select value={owner} onValueChange={setOwner}>
            <SelectTrigger className="w-full sm:w-56">
              <SelectValue placeholder="Alla roller" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="alla">Alla roller</SelectItem>
              {data.owners.map((o) => (
                <SelectItem key={o} value={o}>
                  {o}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={phase} onValueChange={setPhase}>
            <SelectTrigger className="w-full sm:w-56">
              <SelectValue placeholder="Alla skeden" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="alla">Alla skeden</SelectItem>
              {data.byPhase.map((p) => (
                <SelectItem key={p.key} value={p.key}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Section title="Måste finnas oavsett nivå" count={critical.length} tone="warning">
          {critical.map((i) => (
            <ItemRow key={i.docNumber} item={i} onOpenLink={onOpenLink} />
          ))}
        </Section>

        <Section title="Saknas" count={missing.length}>
          {missing.map((i) => (
            <ItemRow key={i.docNumber} item={i} onOpenLink={onOpenLink} />
          ))}
        </Section>

        <Section title="Påbörjat" count={inProgress.length}>
          {inProgress.map((i) => (
            <ItemRow key={i.docNumber} item={i} onOpenLink={onOpenLink} />
          ))}
        </Section>

        {critical.length === 0 && missing.length === 0 && inProgress.length === 0 && (
          <p className="rounded-lg border bg-muted/40 p-3 text-sm text-muted-foreground">
            Inget återstår med de här filtren – allt aktuellt är klart.
          </p>
        )}

        {inactive.length > 0 && (
          <div>
            <Button
              variant="link"
              size="sm"
              className="px-0"
              onClick={() => setShowInactive((v) => !v)}
            >
              <ChevronDown
                className={`mr-1 h-4 w-4 transition-transform ${showInactive ? "rotate-180" : ""}`}
              />
              {showInactive
                ? "Dölj ej aktuella"
                : `Visa ${inactive.length} steg som inte är aktuella på din nivå`}
            </Button>
            {showInactive && (
              <div className="mt-2 space-y-2">
                {inactive.map((i) => (
                  <ItemRow
                    key={i.docNumber}
                    item={i}
                    onOpenLink={onOpenLink}
                    onRestore={busy ? undefined : restore}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CoveragePanel;
